import React, { useContext, useEffect, useState } from 'react'
import { Modal, Button, Alert, Spinner, Table, Badge } from 'react-bootstrap'
import moment from 'moment'
import { useToasts } from 'react-toast-notifications'
import {
  SyncUauInstallmentsResult,
  syncContractInstallmentsWithUau
} from '../../../../services/contracts'
import { convertNumberToCurrencyFormat } from '../../../../utils/convertNumberToCurrencyFormat'
import { AppContext } from '../../../../app'

interface Props {
  show: boolean
  contractId: string
  onClose: () => void
  onApplied: () => void
}

export const SyncUauInstallmentsModal: React.FC<Props> = ({
  show,
  contractId,
  onClose,
  onApplied
}) => {
  const { addToast } = useToasts()
  const { user } = useContext(AppContext)
  const [loading, setLoading] = useState(false)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<SyncUauInstallmentsResult | null>(null)

  useEffect(() => {
    if (show) {
      fetchPreview()
    } else {
      setPreview(null)
      setError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, contractId])

  const fetchPreview = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await syncContractInstallmentsWithUau(
        contractId,
        true,
        user?.token || '',
        user?.selectedContextEncoded
      )
      setPreview(result)
    } catch (err: any) {
      setError(
        err.response?.data?.message || err.message || 'Erro ao consultar UAU'
      )
    } finally {
      setLoading(false)
    }
  }

  const apply = async () => {
    if (!preview) return
    if (
      !window.confirm(
        `Confirmar sincronização? ${preview.toCancel.length} parcela(s) serão CANCELADAS e ${preview.toCreate.length} nova(s) serão CRIADAS no PropToken. Operação irreversível.`
      )
    )
      return
    setApplying(true)
    try {
      const result = await syncContractInstallmentsWithUau(
        contractId,
        false,
        user?.token || '',
        user?.selectedContextEncoded
      )
      if (result.errors && result.errors.length > 0) {
        addToast(
          `Sincronização parcial: ${
            result.toCancel.length -
            result.errors.filter((e) => e.kind === 'cancel').length
          }/${result.toCancel.length} cancel, ${
            result.toCreate.length -
            result.errors.filter((e) => e.kind === 'create').length
          }/${result.toCreate.length} create. Erros: ${result.errors
            .map((e) => e.ref + ': ' + e.message)
            .join('; ')}`,
          { appearance: 'warning', autoDismiss: false }
        )
      } else {
        addToast(
          `Sincronizado: ${result.toCancel.length} cancelada(s), ${result.toCreate.length} criada(s)`,
          { appearance: 'success', autoDismiss: true }
        )
      }
      onApplied()
      onClose()
    } catch (err: any) {
      addToast(
        err.response?.data?.message ||
          err.message ||
          'Erro ao aplicar sincronização',
        { appearance: 'error', autoDismiss: false }
      )
    } finally {
      setApplying(false)
    }
  }

  const nothingToDo =
    preview && preview.toCancel.length === 0 && preview.toCreate.length === 0

  return (
    <Modal show={show} onHide={onClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Sincronizar Parcelas com UAU</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Consultando UAU...</p>
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {preview && !loading && (
          <>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
              Compara parcelas atuais no UAU (Empresa {preview.empresa} / Obra{' '}
              {preview.obra} / Venda {preview.codVenda}) com o PropToken.
              Detecta renegociações, cancelamentos e criações no UAU.
            </p>

            {nothingToDo && (
              <Alert variant="info">
                Tudo sincronizado. {preview.unchanged} parcela(s) OK.
              </Alert>
            )}

            {preview.toCancel.length > 0 && (
              <div className="mb-4">
                <h6 style={{ color: '#c0392b' }}>
                  Parcelas a CANCELAR no PropToken ({preview.toCancel.length})
                </h6>
                <small className="text-muted d-block mb-2">
                  Existem no PropToken mas foram removidas do UAU (renegociadas
                  / substituídas).
                </small>
                <Table striped bordered size="sm" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th>Parcela</th>
                      <th>Tipo</th>
                      <th>Vencimento</th>
                      <th style={{ textAlign: 'right' }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.toCancel.map((c) => (
                      <tr key={c.billingId}>
                        <td>{c.installment_number}</td>
                        <td>
                          <Badge variant="secondary">{c.tipoParcela}</Badge>
                        </td>
                        <td>{moment(c.due_date).format('DD/MM/YYYY')}</td>
                        <td style={{ textAlign: 'right' }}>
                          {convertNumberToCurrencyFormat(c.value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {preview.toCreate.length > 0 && (
              <div className="mb-4">
                <h6 style={{ color: '#27ae60' }}>
                  Parcelas a CRIAR no PropToken ({preview.toCreate.length})
                </h6>
                <small className="text-muted d-block mb-2">
                  Existem no UAU mas não no PropToken (novas parcelas de
                  renegociação).
                </small>
                <Table striped bordered size="sm" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th>Parcela</th>
                      <th>Tipo</th>
                      <th>Vencimento</th>
                      <th style={{ textAlign: 'right' }}>Valor</th>
                      <th>Etapa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.toCreate.map((c) => (
                      <tr key={`${c.parcelaGeral}-${c.tipoParcela}`}>
                        <td>{c.installment_number}</td>
                        <td>
                          <Badge variant="secondary">{c.tipoParcela}</Badge>
                        </td>
                        <td>{moment(c.due_date).format('DD/MM/YYYY')}</td>
                        <td style={{ textAlign: 'right' }}>
                          {convertNumberToCurrencyFormat(c.value)}
                        </td>
                        <td>
                          {c.paymentPartName}
                          {c.isNewPaymentPart && (
                            <Badge
                              variant="warning"
                              className="ml-1"
                              style={{ fontSize: 9 }}
                            >
                              NOVA
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {(preview.skippedPaidInPropToken > 0 ||
              preview.skippedBolecodeEmitted > 0 ||
              preview.skippedTipoNotTracked > 0) && (
              <Alert variant="warning" style={{ fontSize: 13 }}>
                <strong>Ignorados:</strong>
                <ul className="mb-0 mt-1">
                  {preview.skippedPaidInPropToken > 0 && (
                    <li>
                      {preview.skippedPaidInPropToken} parcela(s) já paga(s) no
                      PropToken — não serão canceladas
                    </li>
                  )}
                  {preview.skippedBolecodeEmitted > 0 && (
                    <li>
                      {preview.skippedBolecodeEmitted} parcela(s) com bolecode
                      Itaú emitido — cancele o bolecode primeiro antes de
                      sincronizar
                    </li>
                  )}
                  {preview.skippedTipoNotTracked > 0 && (
                    <li>
                      {preview.skippedTipoNotTracked} parcela(s) de tipo que o
                      UAU não retorna nesta venda (ex: Anual) — não serão
                      tocadas
                    </li>
                  )}
                </ul>
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={applying}>
          Fechar
        </Button>
        {preview && !loading && !nothingToDo && (
          <Button variant="success" onClick={apply} disabled={applying}>
            {applying ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                Aplicando...
              </>
            ) : (
              `Aplicar (${
                preview.toCancel.length + preview.toCreate.length
              } mudanças)`
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
